import type { AdminMetrics } from "@spamjam/contracts";
import { useEffect, useState, type FormEvent } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Activity,
  CircleAlert,
  ExternalLink,
  Gauge,
  LogOut,
  ShieldCheck,
} from "./components/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Select,
  Skeleton,
} from "./components/ui";
import { useLoad } from "./hooks";
import { ApiError, endpoints } from "./lib/api";
import { authClient } from "./lib/auth";
import { LoginPage, VerifyPage } from "./pages";
import { PRODUCT_NAME } from "./product.generated";

export function AdminApp() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route element={<AdminShell />}>
        <Route path="/metrics" element={<AdminMetricsPage />} />
        <Route path="/errors" element={<AdminErrorsPage />} />
      </Route>
      <Route path="/" element={<Navigate replace to="/metrics" />} />
      <Route path="*" element={<Navigate replace to="/metrics" />} />
    </Routes>
  );
}

function AdminShell() {
  const session = useLoad(endpoints.adminSession, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  if (session.loading)
    return (
      <div className="center-screen">
        <Skeleton className="h-12 w-52" />
      </div>
    );
  if (session.error instanceof ApiError && session.error.status === 401)
    return (
      <Navigate
        replace
        to={`/login?${new URLSearchParams({ next: `${location.pathname}${location.search}` })}`}
      />
    );
  if (session.error instanceof ApiError && session.error.status === 403)
    return <AdminAccessDenied />;
  if (session.error)
    return (
      <div className="center-screen admin-status">
        <Alert tone="error">{session.error.message}</Alert>
      </div>
    );
  const user = session.data!.user;
  return (
    <div className="admin-frame">
      <a className="skip-link" href="#admin-main">
        Skip to main content
      </a>
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span className="logo-mark">
            <ShieldCheck size={18} />
          </span>
          <div>
            <strong>{PRODUCT_NAME}</strong>
            <span>Admin</span>
          </div>
        </div>
        <nav aria-label="Admin navigation">
          <NavLink
            to="/metrics"
            className={({ isActive }) =>
              `nav-link ${isActive ? "nav-link-active" : ""}`
            }
          >
            <Gauge size={18} /> Metrics
          </NavLink>
          <NavLink
            to="/errors"
            className={({ isActive }) =>
              `nav-link ${isActive ? "nav-link-active" : ""}`
            }
          >
            <CircleAlert size={18} /> Errors
          </NavLink>
        </nav>
        <div className="admin-identity">
          <div className="avatar">{user.email[0]?.toUpperCase()}</div>
          <div>
            <strong>{user.name || "Admin"}</strong>
            <span>{user.email}</span>
          </div>
          <Button
            variant="ghost"
            aria-label="Sign out"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              const result = await authClient.signOut();
              if (result.error) setSigningOut(false);
              else navigate("/login", { replace: true });
            }}
          >
            <LogOut size={18} />
          </Button>
        </div>
      </aside>
      <main id="admin-main" className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}

function AdminAccessDenied() {
  const [busy, setBusy] = useState(false);
  return (
    <div className="center-screen admin-status">
      <Card>
        <CircleAlert size={24} />
        <h1>Admin access required</h1>
        <p>This account is not authorized to use the {PRODUCT_NAME} admin.</p>
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await authClient.signOut();
            window.location.replace("/login");
          }}
        >
          Sign in with the admin account
        </Button>
      </Card>
    </div>
  );
}

const metricCards: Array<{
  key: keyof AdminMetrics;
  label: string;
  description: string;
}> = [
  { key: "users", label: "Users", description: "Accounts created" },
  {
    key: "mailboxesConnected",
    label: "Mailboxes connected",
    description: "Gmail connections added",
  },
  {
    key: "emailsProcessed",
    label: "Emails processed",
    description: "Classification decisions",
  },
  {
    key: "emailsMarkedSpam",
    label: "Emails marked spam",
    description: "Confirmed Gmail spam actions",
  },
  {
    key: "emailsMarkedNotSpam",
    label: "Marked not spam",
    description: "User false-positive corrections",
  },
];

function AdminMetricsPage() {
  const range = useAdminRange(30);
  const report = useLoad(
    () => endpoints.adminMetrics(range.from, range.to),
    [range.from, range.to],
  );
  return (
    <AdminPage
      eyebrow="Product health"
      title="Metrics"
      description="Product activity recorded during the selected date range."
      range={<DateRangeSelector {...range} presets={[1, 7, 30, 90]} />}
    >
      {report.error && <Alert tone="error">{report.error.message}</Alert>}
      <div className="admin-metric-grid">
        {metricCards.map((metric) => (
          <Card className="admin-metric-card" key={metric.key}>
            <div className="admin-metric-label">
              <Activity size={16} /> {metric.label}
            </div>
            {report.loading ? (
              <Skeleton className="h-10 w-24" />
            ) : (
              <strong>
                {report.data
                  ? report.data.metrics[metric.key].toLocaleString()
                  : "—"}
              </strong>
            )}
            <span>{metric.description}</span>
          </Card>
        ))}
      </div>
    </AdminPage>
  );
}

function AdminErrorsPage() {
  const range = useAdminRange(1);
  const report = useLoad(
    () => endpoints.adminErrors(range.from, range.to),
    [range.from, range.to],
  );
  return (
    <AdminPage
      eyebrow="Cloudflare Observability"
      title="Errors"
      description="Exceptions, error logs, failed outcomes, and 5xx responses from the product Workers."
      range={<DateRangeSelector {...range} presets={[1, 3, 7]} />}
      action={
        report.data ? (
          <Button
            variant="secondary"
            render={
              <a
                href={report.data.cloudflareUrl}
                target="_blank"
                rel="noreferrer"
              />
            }
          >
            Open Cloudflare <ExternalLink size={15} />
          </Button>
        ) : null
      }
    >
      <Alert>
        Cloudflare retains detailed log occurrences for seven days. Times below
        are shown in your local timezone.
      </Alert>
      {report.error && <Alert tone="error">{report.error.message}</Alert>}
      {report.error ? null : report.loading ? (
        <Card className="admin-error-loading">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-3/4" />
        </Card>
      ) : report.data?.errors.length ? (
        <Card className="admin-error-list">
          {report.data.errors.map((error) => (
            <article className="admin-error-row" key={error.id}>
              <div className="admin-error-severity" aria-hidden="true">
                <CircleAlert size={17} />
              </div>
              <div className="admin-error-copy">
                <div>
                  <strong>{error.message}</strong>
                  <Badge tone="bad">
                    {error.statusCode
                      ? `HTTP ${error.statusCode}`
                      : error.level}
                  </Badge>
                </div>
                <span>
                  {error.service} · {new Date(error.timestamp).toLocaleString()}
                  {error.outcome ? ` · ${error.outcome}` : ""}
                </span>
                {(error.rayId || error.requestId) && (
                  <code>{error.rayId ?? error.requestId}</code>
                )}
              </div>
              <Button
                variant="ghost"
                render={
                  <a
                    href={error.cloudflareUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${error.service} issue in Cloudflare`}
                  />
                }
              >
                Investigate <ExternalLink size={14} />
              </Button>
            </article>
          ))}
        </Card>
      ) : (
        <Card className="empty admin-empty">
          <ShieldCheck size={24} />
          <h3>No errors in this range</h3>
          <p>Cloudflare did not return any matching Worker errors.</p>
        </Card>
      )}
    </AdminPage>
  );
}

function AdminPage({
  eyebrow,
  title,
  description,
  range,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  range: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {action}
      </header>
      {range}
      <div className="admin-page-body">{children}</div>
    </div>
  );
}

function DateRangeSelector({
  from,
  to,
  setRange,
  presets,
}: ReturnType<typeof useAdminRange> & { presets: number[] }) {
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, to]);
  const activePreset = presets.find((days) => {
    const expected = dateDaysAgo(to, days - 1);
    return expected === from;
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draftFrom <= draftTo) setRange(draftFrom, draftTo);
  };
  return (
    <form className="admin-range" onSubmit={submit}>
      <Field label="Preset">
        <Select
          aria-label="Date range preset"
          value={activePreset ? String(activePreset) : "custom"}
          onChange={(event) => {
            if (event.target.value === "custom") return;
            const days = Number(event.target.value);
            setRange(dateDaysAgo(todayDate(), days - 1), todayDate());
          }}
        >
          {presets.map((days) => (
            <option value={days} key={days}>
              {days === 1 ? "Today" : `Last ${days} days`}
            </option>
          ))}
          <option value="custom">Custom</option>
        </Select>
      </Field>
      <Field label="From">
        <Input
          type="date"
          required
          max={draftTo}
          value={draftFrom}
          onChange={(event) => setDraftFrom(event.target.value)}
        />
      </Field>
      <Field label="To">
        <Input
          type="date"
          required
          min={draftFrom}
          max={todayDate()}
          value={draftTo}
          onChange={(event) => setDraftTo(event.target.value)}
        />
      </Field>
      <Button type="submit" variant="secondary">
        Apply
      </Button>
    </form>
  );
}

function useAdminRange(defaultDays: number) {
  const [params, setParams] = useSearchParams();
  const today = todayDate();
  const validDate = (value: string | null) =>
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  const to = validDate(params.get("to")) ?? today;
  const from =
    validDate(params.get("from")) ?? dateDaysAgo(to, defaultDays - 1);
  return {
    from,
    to,
    setRange(nextFrom: string, nextTo: string) {
      setParams({ from: nextFrom, to: nextTo });
    },
  };
}

function todayDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function dateDaysAgo(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() - days);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}
